import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useGlobalLanguage, useTranslation } from "../i18n";
import { useTheme } from "../hooks/useTheme";
import { ThemeToggle } from "@components/ui";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useNavbarItemPermissions } from "../permissions/useNavPermissions";
import { isSidebarPathActive } from "./sidebarPath";
import { COMPANY_LOGIN_PATH } from "../config/authPaths";
import { useQueryClient } from "@tanstack/react-query";
import { logoutStorefrontSession } from "../services/sessionService";
import { resolveAssetUrl } from "../utils/mediaUrl";
import {
  dashboardPath,
  storefrontRoot,
} from "../config/storefrontRouting";

interface SubItem {
  labelKey: string;
  path: string;
  icon?: React.ReactNode;
  permKey?: string;
}

interface NavItem {
  labelKey: string;
  path?: string;
  icon: React.ReactNode;
  children?: SubItem[];
  permKey?: string;
}

const CircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-2 h-2 shrink-0">
    <circle cx="12" cy="12" r="5" />
  </svg>
);

const CartIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="w-4 h-4 shrink-0"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const ALL_NAV_ITEMS: NavItem[] = [
  {
    labelKey: "sidebar.home",
    path: "/overview",
    permKey: "canAccessHome",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    labelKey: "sidebar.settings",
    path: "/settings",
    permKey: "canAccessSettings",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14M12 2v2M12 20v2M2 12h2M20 12h2" />
      </svg>
    ),
  },
  {
    labelKey: "sidebar.notifications",
    path: "/user-notifications",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    labelKey: "sidebar.peopleAndAccess",
    permKey: "canAccessUsersAndRules",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    children: [
      {
        labelKey: "sidebar.teamMembers",
        path: "/users-rules/users",
        icon: <CircleIcon />,
        permKey: "canAccessStaffUsersAndRoles",
      },
      {
        labelKey: "sidebar.websiteAccounts",
        path: "/website-users",
        icon: <CircleIcon />,
        permKey: "canAccessWebsiteUsers",
      },
      {
        labelKey: "sidebar.rolesPermissions",
        path: "/users-rules/rules",
        icon: <CircleIcon />,
        permKey: "canAccessStaffUsersAndRoles",
      },
    ],
  },
  {
    labelKey: "sidebar.generalCodes",
    permKey: "canAccessGeneralCodes",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    children: [
      {
        labelKey: "sidebar.generalCodes.payment",
        path: "/general-codes/payment",
        icon: <CircleIcon />,
      },
      {
        labelKey: "sidebar.generalCodes.orderType",
        path: "/general-codes/order-type",
        icon: <CircleIcon />,
      },
      {
        labelKey: "sidebar.generalCodes.pos",
        path: "/general-codes/pos",
        icon: <CartIcon />,
      },
    ],
  },
  {
    labelKey: "sidebar.products",
    permKey: "canAccessProducts",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
    children: [
      {
        labelKey: "sidebar.products.categories",
        path: "/products/categories",
        icon: <CircleIcon />,
        permKey: "canAccessCategories",
      },
      {
        labelKey: "sidebar.products.list",
        path: "/products/list",
        icon: <CircleIcon />,
        permKey: "canAccessProductList",
      },
      {
        labelKey: "sidebar.products.priceRules",
        path: "/products/price-rules",
        icon: <CircleIcon />,
        permKey: "canAccessProductList",
      },
      {
        labelKey: "sidebar.products.promotions",
        path: "/commerce/promotions",
        icon: <CircleIcon />,
        permKey: "canAccessPromotions",
      },
      {
        labelKey: "sidebar.products.tags",
        path: "/products/tags",
        icon: <CircleIcon />,
        permKey: "canAccessProductList",
      },
      {
        labelKey: "sidebar.products.variantAttributes",
        path: "/products/variant-attributes",
        icon: <CircleIcon />,
        permKey: "canAccessProductList",
      },
      {
        labelKey: "sidebar.products.testimonials",
        path: "/website/testimonials",
        icon: <CircleIcon />,
        permKey: "canAccessTestimonials",
      },
    ],
  },
  {
    labelKey: "sidebar.contactInquiries",
    path: "/contact-inquiries",
    permKey: "canAccessContactInquiries",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </svg>
    ),
  },
  {
    labelKey: "sidebar.salesSessions",
    path: "/sales-sessions",
    permKey: "canAccessSalesSessions",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
  {
    labelKey: "sidebar.commerce",
    permKey: "canAccessCommerce",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    children: [
      {
        labelKey: "sidebar.orderSearch",
        path: "/orders",
        icon: <CircleIcon />,
        permKey: "canAccessOrderSearch",
      },
      {
        labelKey: "sidebar.commerce.customerRisk",
        path: "/commerce/customer-risk",
        icon: <CircleIcon />,
        permKey: "canAccessCustomerRisk",
      },
      {
        labelKey: "sidebar.commerce.reviews",
        path: "/commerce/reviews",
        icon: <CircleIcon />,
        permKey: "canAccessCommerceReviews",
      },
    ],
  },
  {
    labelKey: "sidebar.returns",
    permKey: "canAccessReturns",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <polyline points="1 4 1 10 7 10" />
        <path d="M3.51 15a9 9 0 1 0 .49-3.31" />
      </svg>
    ),
    children: [
      {
        labelKey: "sidebar.returns.list",
        path: "/returns/list",
        icon: <CircleIcon />,
      },
      {
        labelKey: "sidebar.returns.add",
        path: "/returns/add",
        icon: <CircleIcon />,
      },
    ],
  },
  {
    labelKey: "sidebar.inventory",
    path: "/commerce/inventory",
    permKey: "canAccessInventory",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        <line x1="12" y1="12" x2="12" y2="16" />
        <line x1="10" y1="14" x2="14" y2="14" />
      </svg>
    ),
  },
  {
    labelKey: "sidebar.reports",
    path: "/commerce/reports",
    permKey: "canAccessReports",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="w-[18px] h-[18px] shrink-0"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
];

function SidebarItem({
  item,
  t,
}: {
  item: NavItem;
  t: (key: string) => string;
}) {
  const location = useLocation();
  const hasChildren = !!(item.children && item.children.length > 0);
  const isChildActive = hasChildren
    ? item.children!.some((child) =>
        isSidebarPathActive(location.pathname, dashboardPath(child.path)),
      )
    : false;
  const isActive = item.path
    ? isSidebarPathActive(location.pathname, dashboardPath(item.path))
    : isChildActive;
  const [manuallyOpen, setManuallyOpen] = useState(false);
  const open = isChildActive || manuallyOpen;

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setManuallyOpen((value) => !value)}
          aria-expanded={open}
          data-active={isActive ? "true" : undefined}
          className={`nav-underline nav-underline-block w-full items-center justify-between gap-3 px-4 py-3 text-[13px] font-medium text-brand-on-brand transition-colors duration-300`}
        >
          <div className="flex items-center gap-3">
            <span className="text-brand-on-brand">{item.icon}</span>
            <span>{t(item.labelKey)}</span>
          </div>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            className="w-3 h-3 shrink-0 transition-transform duration-200"
            style={{
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              opacity: 0.55,
            }}
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div
          className="overflow-hidden bg-brand-on-brand/10 transition-[max-height] duration-300 ease-in-out"
          style={{
            maxHeight: open ? `${item.children!.length * 44}px` : "0px",
          }}
        >
          {item.children!.map((child) => {
            const childPath = dashboardPath(child.path);
            const childActive = isSidebarPathActive(
              location.pathname,
              childPath,
            );
            return (
              <Link
                key={child.path}
                to={childPath}
                aria-current={childActive ? "page" : undefined}
                data-active={childActive ? "true" : undefined}
                className={`nav-underline nav-underline-block w-full items-center gap-3 px-5 py-2.5 ps-7 text-[12.5px] text-brand-on-brand transition-colors duration-300 ${
                  childActive ? "font-semibold" : "font-normal"
                }`}
              >
                <span>{child.icon}</span>
                <span>{t(child.labelKey)}</span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Link
      to={dashboardPath(item.path!)}
      aria-current={isActive ? "page" : undefined}
      data-active={isActive ? "true" : undefined}
      className={`nav-underline nav-underline-block w-full items-center gap-3 px-4 py-3 text-[13px] text-brand-on-brand transition-colors duration-300 ${
        isActive ? "font-semibold" : "font-medium"
      }`}
    >
      <span className="text-brand-on-brand">{item.icon}</span>
      <span>{t(item.labelKey)}</span>
    </Link>
  );
}

function Layout() {
  const { t, dir, isRTL, language } = useTranslation();
  const { toggleLanguage } = useGlobalLanguage();
  useTheme();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const principalType = useAppSelector((s) => s.auth.principalType);
  const companyName = useAppSelector((s) => s.company.name);
  const companyNameAr = useAppSelector((s) => s.company.nameAr);
  const localizedCompanyName =
    (language === "ar" ? companyNameAr : companyName)?.trim() || companyName;
  const companySlug = useAppSelector((s) => s.company.slug);
  const companyLogoUrl = useAppSelector((s) => s.company.logoUrl);
  const userName = useAppSelector((s) => {
    const user = s.auth?.user;
    return (
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      user?.username ||
      user?.email ||
      s.company.name ||
      ""
    );
  });
  const location = useLocation();
  const [sidebarOpenPath, setSidebarOpenPath] = useState<string | null>(null);
  const sidebarOpen = sidebarOpenPath === location.pathname;
  const sidebarRef = useRef<HTMLElement>(null);
  const perms = useNavbarItemPermissions();
  const handleLogout = () => {
    void logoutStorefrontSession({
      principalType,
      dispatch,
      queryClient,
    });
    navigate(COMPANY_LOGIN_PATH);
  };

  const navItems = useMemo(() => {
    const p = perms as Record<string, boolean>;
    return ALL_NAV_ITEMS.filter((item) => !item.permKey || p[item.permKey])
      .map((item) => {
        if (!item.children) return item;
        const filtered = item.children.filter(
          (c) => !c.permKey || p[c.permKey],
        );
        if (filtered.length === 0) return null;
        return { ...item, children: filtered };
      })
      .filter(Boolean) as NavItem[];
  }, [perms]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        sidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        setSidebarOpenPath(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarOpen]);

  const sidebarSide = isRTL ? "right-0" : "left-0";
  const sidebarTranslate = isRTL
    ? sidebarOpen
      ? "translate-x-0"
      : "translate-x-full"
    : sidebarOpen
      ? "translate-x-0"
      : "-translate-x-full";

  const initials = userName
    ? userName
        .split(" ")
        .map((w: string) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "U";

  return (
    <div
      dir={dir}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "var(--background)",
      }}
      className="text-foreground"
    >
      {/* ─── Navbar ──────────────────────────────────────────────────────── */}
      <header
        role="banner"
        className="flex items-center justify-between gap-4 bg-brand-primary px-5 text-brand-on-brand"
        style={{
          height: "60px",
          minHeight: "60px",
          zIndex: 50,
          borderBottom:
            "1px solid color-mix(in srgb, var(--brand-on-brand) 12%, transparent)",
          boxShadow:
            "0 2px 12px color-mix(in srgb, var(--foreground) 18%, transparent)",
        }}
      >
        {/* Left side: Logo + App name */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            aria-label={t("header.openMenu")}
            className="lg:hidden p-2 rounded-lg hover:opacity-80 transition-opacity"
            onClick={() =>
              setSidebarOpenPath(sidebarOpen ? null : location.pathname)
            }
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-5 h-5 text-brand-on-brand"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          {/* Brand / Logo */}
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-brand-on-brand/15 shadow-sm">
              {companyLogoUrl ? (
                <img
                  src={
                    resolveAssetUrl(companyLogoUrl, {
                      width: 80,
                      height: 80,
                      quality: 80,
                    }) ?? undefined
                  }
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-5 w-5 text-brand-on-brand"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              )}
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-[13px] font-bold tracking-wide text-brand-on-brand">
                {localizedCompanyName}
              </span>
             
            </div>
          </div>
        </div>

        {/* Center: Quick nav links (desktop) */}
        <nav
          aria-label={t("header.mainNav")}
          className="hidden lg:flex items-center gap-1"
        >
          {[
            ...(perms.canAccessHome
              ? [{ to: dashboardPath("/overview"), label: t("sidebar.home") }]
              : []),
            ...(perms.canAccessSettings
              ? [{ to: dashboardPath("/settings"), label: t("sidebar.settings") }]
              : []),
            ...(companySlug
              ? [
                  {
                    to: storefrontRoot(companySlug),
                    label: t("dashboard.viewWebsite"),
                  },
                ]
              : []),
          ].map(({ to, label }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                aria-current={active ? "page" : undefined}
                data-active={active ? "true" : undefined}
                className="nav-underline px-3.5 py-1.5 text-[13px] font-medium text-brand-on-brand transition-colors duration-300"
              >
                {label}
              </Link>
            );
          })}
          <button
            type="button"
            className="nav-underline px-3.5 py-1.5 text-[13px] font-medium text-brand-on-brand transition-colors duration-300 cursor-pointer"
            onClick={handleLogout}
          >
            {t("header.logout")}
          </button>
        </nav>

        {/* Right side: Search + Notifications + User + Lang */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Search */}
          <div className="relative hidden md:block">
            <input
              type="search"
              placeholder={t("header.search")}
              aria-label={t("header.searchSystem")}
              className="w-44 rounded-xl px-4 py-2 text-[12.5px] text-brand-on-brand transition-all duration-200 focus:w-56 focus:outline-none"
              style={{
                background:
                  "color-mix(in srgb, var(--brand-on-brand) 12%, transparent)",
                border:
                  "1px solid color-mix(in srgb, var(--brand-on-brand) 18%, transparent)",
                paddingInlineEnd: "32px",
              }}
              onFocus={(e) => {
                (e.target as HTMLInputElement).style.border =
                  "1px solid color-mix(in srgb, var(--brand-on-brand) 45%, transparent)";
                (e.target as HTMLInputElement).style.background =
                  "color-mix(in srgb, var(--brand-on-brand) 16%, transparent)";
              }}
              onBlur={(e) => {
                (e.target as HTMLInputElement).style.border =
                  "1px solid color-mix(in srgb, var(--brand-on-brand) 18%, transparent)";
                (e.target as HTMLInputElement).style.background =
                  "color-mix(in srgb, var(--brand-on-brand) 12%, transparent)";
              }}
            />
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand-on-brand"
              style={{
                [isRTL ? "left" : "right"]: "10px",
              }}
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>

          {/* Notifications */}
          <button
            aria-label={t("header.notifications")}
            className="relative flex h-9 w-9 items-center justify-center rounded-xl border border-brand-on-brand/15 bg-brand-on-brand/10 text-brand-on-brand transition-colors hover:bg-brand-on-brand/18"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-4.5 w-4.5 text-brand-on-brand"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span
              className="absolute top-1 h-2 w-2 rounded-full border border-brand-primary bg-danger"
              style={{ [isRTL ? "right" : "left"]: "5px" }}
              aria-hidden="true"
            />
          </button>

          {/* Divider */}
          <div className="hidden h-6 w-px bg-brand-on-brand/20 sm:block" />

          {/* Theme toggle (light/dark) */}
          <ThemeToggle />

          {/* User avatar + name */}
          <div className="hidden cursor-pointer items-center gap-2.5 rounded-xl border border-brand-on-brand/15 bg-brand-on-brand/10 px-2.5 py-1.5 transition-colors hover:bg-brand-on-brand/18 sm:flex">
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold"
              style={{
                background:
                  "color-mix(in srgb, var(--brand-on-brand) 18%, transparent)",
                color: "var(--brand-on-brand)",
              }}
            >
              {initials}
            </div>
            <span className="hidden max-w-[100px] truncate text-[12.5px] font-medium text-brand-on-brand lg:block">
              {userName}
            </span>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              className="hidden h-3 w-3 text-brand-on-brand lg:block"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>

          {/* Language toggle */}
          <button
            aria-label={t("header.language")}
            onClick={toggleLanguage}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-brand-on-brand/15 bg-brand-on-brand/10 text-[12px] font-bold text-brand-on-brand transition-colors hover:bg-brand-on-brand/18"
          >
            {language === "ar" ? "EN" : "AR"}
          </button>
        </div>
      </header>

      {/* ─── Body (Sidebar + Main) ─────────────────────────────────────── */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-overlay z-30 lg:hidden"
            aria-hidden="true"
            onClick={() => setSidebarOpenPath(null)}
          />
        )}

        {/* ─── Sidebar ───────────────────────────────────────────────────── */}
        <aside
          ref={sidebarRef}
          role="navigation"
          aria-label={t("header.sidebarNav")}
          className={[
            `fixed top-[60px] ${sidebarSide} z-40`,
            "h-[calc(100vh-60px)] transition-transform duration-300 ease-in-out",
            "lg:static lg:top-auto lg:h-full lg:translate-x-0",
            `${sidebarTranslate} lg:translate-x-0`,
          ].join(" ")}
          style={{
            width: "248px",
            minWidth: "248px",
            background: "var(--brand-primary)",
            overflowY: "auto",
            overflowX: "hidden",
            borderInlineEnd:
              "1px solid color-mix(in srgb, var(--brand-on-brand) 12%, transparent)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Sidebar category label */}
          <div className="px-4 pt-5 pb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-on-brand">
              {t("header.mainNav")}
            </p>
          </div>

          {/* Nav items */}
          <nav
            aria-label={t("header.navLinks")}
            className="flex-1 pb-4 text-brand-on-brand"
          >
            {navItems.map((item) => (
              <SidebarItem key={item.labelKey} item={item} t={t} />
            ))}
          </nav>

          {/* Sidebar footer */}
          <div
            className="mt-auto px-4 py-3"
            style={{
              borderTop:
                "1px solid color-mix(in srgb, var(--brand-on-brand) 12%, transparent)",
            }}
          >
            {companySlug && (
              <Link
                to={storefrontRoot(companySlug)}
                className="mb-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[12.5px] font-medium text-brand-on-brand transition-opacity hover:opacity-80"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-4 w-4 shrink-0"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
                </svg>
                {t("dashboard.viewWebsite")}
              </Link>
            )}
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[12.5px] font-medium text-brand-on-brand transition-colors hover:text-danger"
              onClick={handleLogout}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-4 w-4 shrink-0"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              {t("header.logout")}
            </button>
          </div>
        </aside>

        {/* ─── Main content ─────────────────────────────────────────────── */}
        <main
          id="main-content"
          role="main"
          style={{
            flex: 1,
            overflowY: "auto",
            minWidth: 0,
            background: "var(--background)",
          }}
          className="p-5 lg:p-7"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
